;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	controlCreateDataProvider,
	controlDeleteDataProvider,
	controlEditDataProvider,
} from "@/components/settings/data-providers";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { emitter } from "@/lib/emitter";
import { logger } from "@/lib/services/logger";
import { invalidateAllQueries } from "@/queries/config/invalidate-all-queries";
import {
	type DataProvider,
	getDataProvidersQueryOptions,
} from "@/queries/data-provider/fetchers";
import {
	createDataProvider,
	deleteDataProvider,
	editDataProvider,
	pingDataProvider,
} from "@/queries/data-provider/updates";
import { userMutationKeys } from "@/queries/query-keys/user";

interface UseDataProvidersOptions {
	enabled?: boolean;
}

type WorkspaceDataProviders = Omit<DataProvider, "apiKey" | "clientSecret">[];

/** Shared reference so consumers memoizing on `dataProviders` do not recompute every render while the query is empty. */
const EMPTY_DATA_PROVIDERS: WorkspaceDataProviders = [];

export function useWorkspaceDataProviders(
	workspaceId: string,
	options?: UseDataProvidersOptions,
) {
	const dataProvidersQueryOptions = getDataProvidersQueryOptions(workspaceId);

	const {
		data,
		isLoading: loading,
		error,
		refetch: refreshProviders,
		...rest
	} = useQuery({
		...dataProvidersQueryOptions,
		enabled: (options?.enabled ?? true) && dataProvidersQueryOptions.enabled,
	});

	const dataProviders = data ?? EMPTY_DATA_PROVIDERS;

	const {
		mutateAsync: createWorkspaceDataProvider,
		isPending: creatingWorkspaceDataProvider,
	} = useMutation({
		mutationFn: createDataProvider,
		mutationKey: userMutationKeys.createDataProvider(workspaceId),
		onError: async (mutationError, variables) => {
			logger.captureUnexpected(
				"[createWorkspaceDataProvider] error",
				mutationError,
				{
					feature: "data-providers",
					providerType: variables.data.type,
					workspaceId,
				},
			);
			await invalidateAllQueries();
			toast.error("Failed to set up data provider. Please try again.");
		},
		onSuccess: async (_data, variables) => {
			await invalidateAllQueries();
			toast.success("Data provider setup successfully!");
			emitter.emit("settings.workspace.data_provider.create_success", {
				providerType: variables.data.type,
			});
			controlCreateDataProvider({ state: "off" });
		},
	});

	const {
		mutateAsync: editWorkspaceDataProvider,
		isPending: editingWorkspaceDataProvider,
	} = useMutation({
		mutationFn: editDataProvider,
		mutationKey: userMutationKeys.editDataProvider(workspaceId),
		onError: (error) => {
			logger.captureUnexpected("[editWorkspaceDataProvider] error", error, {
				feature: "data-providers",
				workspaceId,
			});
			toast.error("Failed to update credentials");
		},
		onSuccess: async () => {
			await invalidateAllQueries();
			toast.success("Credentials updated successfully");
			controlEditDataProvider({ data: { providerId: null }, state: "off" });
		},
	});

	const {
		variables,
		mutateAsync: deleteWorkspaceDataProvider,
		isPending: deletingWorkspaceDataProvider,
	} = useMutation({
		mutationFn: deleteDataProvider,
		mutationKey: userMutationKeys.deleteDataProvider(workspaceId),
		onError: (error) => {
			logger.captureUnexpected("[deleteWorkspaceDataProvider] error", error, {
				feature: "data-providers",
				workspaceId,
			});
			toast.error("Failed to delete data provider");
		},
		onSuccess: async () => {
			await invalidateAllQueries();
			toast.success("Data provider deleted successfully");
			controlDeleteDataProvider({ data: { providerId: null }, state: "off" });
		},
	});

	const {
		mutateAsync: pingWorkspaceDataProvider,
		isPending: pingingWorkspaceDataProvider,
	} = useMutation({
		mutationFn: pingDataProvider,
		mutationKey: userMutationKeys.pingDataProvider(workspaceId),
	});

	return {
		...rest,
		createWorkspaceDataProvider,
		creatingWorkspaceDataProvider,
		dataProviders,
		dataProvidersQueryOptions,
		deletedProviderId: variables?.providerId,
		deleteWorkspaceDataProvider,
		deletingWorkspaceDataProvider,
		editingWorkspaceDataProvider,
		editWorkspaceDataProvider,
		error,
		loading,
		pingingWorkspaceDataProvider,
		pingWorkspaceDataProvider,
		refreshProviders,
	};
}

export function useDataProviders(options?: UseDataProvidersOptions) {
	const { workspaceId } = useCurrentWorkspace();
	return useWorkspaceDataProviders(workspaceId, options);
}
